/* eslint-disable @typescript-eslint/no-unsafe-argument */
import {
  getCompensation,
  getCustomGroupMetadata,
  getDemographic,
  getFactors,
  getUserCustomGroupsData,
  postCompensationData,
  postCustomGroupData,
  postDemographicData,
  postFactors,
  removeCustomGroup,
} from 'services/elements';
// import { ICountryListResponse } from 'services/countryList';
import { COMPENSATION_NAME, isVariablePayStiOrLtiByName } from 'constants/base-elements.constants';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

function deriveIncludeZeroVariablePayFromApiList(variablePayList: any[],
): boolean | null {
  if (!variablePayList?.length) return true;
  const flags = variablePayList
    .map((v: any) => v.includeZeroVariablePay)
    .filter((x: unknown) => x !== null && x !== undefined) as boolean[];
  if (flags.length === 0) return null;
  return flags.every(Boolean);
}

const LS_INCLUDE_ZERO_VP = 'kf1.newReport.includeZeroVariablePayByReportId';

function loadIncludeZeroVariablePayMap(): Record<string, boolean> {
  if (typeof window === 'undefined' || !window.localStorage) {
    return {};
  }
  try {
    const raw = localStorage.getItem(LS_INCLUDE_ZERO_VP);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, boolean>;
    }
    return {};
  } catch {
    return {};
  }
}

function readIncludeZeroVariablePayForReport(reportId: unknown): boolean | undefined {
  const id = String(Number(reportId) || reportId);
  if (!id) return undefined;
  const m = loadIncludeZeroVariablePayMap();
  if (Object.prototype.hasOwnProperty.call(m, id)) {
    return Boolean(m[id]);
  }
  return undefined;
}

function writeIncludeZeroVariablePayForReport(reportId: unknown, value: boolean): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    const id = String(Number(reportId) || reportId);
    if (!id) return;
    const next = { ...loadIncludeZeroVariablePayMap(), [id]: value };
    localStorage.setItem(LS_INCLUDE_ZERO_VP, JSON.stringify(next));
  } catch {
  }
}

type INewReportStore = {
  compensation: any;
  variablePay: any[];
  compensationResponse: any;
  selectedCompensation: any;
  selectedVariablePay: number[];
  setSelectedVariablePay: (ids: number[]) => void;
  includeZeroVariablePay: boolean;
  setIncludeZeroVariablePay: (value: boolean) => void;
  error: any;
  isLoading: boolean;
  getCompensation: (reportId: any) => void;
  postCompensation: (reportId: any, props: any) => void;
  setCompensation: (selectedCompensation: any[]) => void;
  resetReportStore: (reportId?: any) => any;
  demographic: any;
  demographicResponse: any;
  selectedDemographic: any;
  postCustomGroupResponse: any;
  getDemographic: (reportId: any) => void;
  postDemographic: (reportId: any, props: any) => void;
  postCustomGroupData: (reportId: any, props: any) => void;
  clearPreviousPostResponse: () => void;
  removeGroupFromList: (groupId: any, reportId: any) => void;
  setDemographic: (selectedDemographic: number[]) => void;
  reportId: any;
  lastCompensationFetchReportId: number | null;
  setReportId: (data: any) => void;
  customGroupMetadata: any;
  getCustomGroupMetadata: (reportId: any) => void;
  setCustomGroupMetadata: (data: any) => void;
  userCustomGroups: any;
  selectedUserCustomGroup: any;
  setSelectedUserCustomGroup: any;
  getUserCustomGroups: (reportId: any) => void;
  setUserCustomGroups: (data: any) => void;
  userSelectedMetadata: any;
  setUserSelectedMetadata: (data: any) => void;
  activatedStepsStore: any;
  updateActivationStepsStore: (step: any) => void;
  activatedSubStepsStore: any;
  updateActivatedSubStepsStore: (step: any) => void;
  groupList: any;
  setGroupList: (groupList: any[]) => void;
  factorsList: any;
  selectedfactors: any;
  getFactors: (id: any) => void;
  updateFactors: (id: any, props: any) => void;
  setFactors: (list?: any[], selectedfactors?: number[]) => void;
  setIsLoading: any;
  isUpdateModeButton: boolean;
  setIsUpdateModeButton: any;
  isAllOtherEmployees: boolean;
  updateIsAllOtherEmployeesAdded: (data: any) => void;
  dataValidationKey: any;
  updateDataValidationKey: (data: any) => void;
};
const initialState = {
  compensation: [],
  variablePay: [],
  selectedCompensation: [1],
  selectedVariablePay: [],
  includeZeroVariablePay: true,
  isLoading: false,
  demographic: [],
  selectedDemographic: null,
  customGroupMetadata: [],
  userCustomGroups: [],
  userSelectedMetadata: null,
  selectedUserCustomGroup: null,
  postCustomGroupResponse: null,
  error: null,
  reportId: null,
  lastCompensationFetchReportId: null,
  activatedStepsStore: 1,
  activatedSubStepsStore: 0,
  selectedfactors: null,
  groupList: [],
  factorsList: [],
  isUpdateModeButton: true,
  dataValidationKey: 1,
  isAllOtherEmployees: false,
};

//  needs to be optimized
const useNewReportStore = create<INewReportStore, [['zustand/persist', unknown]]>(
  persist(
    (set, get) => ({
      ...initialState,
      updateActivationStepsStore: (step: any) => set({ activatedStepsStore: step }),
      updateActivatedSubStepsStore: (step: any) => set({ activatedSubStepsStore: step }),
      compensationResponse: {},
      demographicResponse: {},
      postCustomGroupResponse: {},
      userCustomGroups: {},
      setIsUpdateModeButton: (data: any) => set({ isUpdateModeButton: data }),
      setCompensation: (data: any) => set({ selectedCompensation: data }),
      setSelectedVariablePay: (data: number[]) => set({ selectedVariablePay: data }),
      setIncludeZeroVariablePay: (value: boolean) => {
        set((state) => {
          if (state.reportId != null) {
            writeIncludeZeroVariablePayForReport(state.reportId, value);
          }
          return { includeZeroVariablePay: value };
        });
      },
      setUserSelectedMetadata: (data: any) => set({ userSelectedMetadata: data }),
      setSelectedUserCustomGroup: (data: any) => ({ selectedUserCustomGroup: data }),
      setReportId: (data: any) => set({ reportId: data }),
      updateDataValidationKey: (data: any) => set({ dataValidationKey: data }),
      updateIsAllOtherEmployeesAdded: (data: any) => set({ isAllOtherEmployees: data }),
      getCompensation: async (reportId: any): Promise<any> => {
        try {
          set({ isLoading: true });
          const response: any = await getCompensation(reportId);
          const { responseCode, data } = response;
          if (responseCode === 200 && data) {
            const compensationElement = data?.currentReport?.compensationElement ?? data?.parentReport?.compensationElement ?? [];
            const variablePayList = data?.currentReport?.variablePay ?? data?.parentReport?.variablePay ?? [];

            const newData: any = compensationElement.map((value: any) => {
              if (value.compensationName === COMPENSATION_NAME.BASE_SALARY) {
                return {
                  ...value,
                  isFlag: true,
                  selected: false,
                };
              }
              return {
                ...value,
                selected: false,
              };
            });

            const selectedFromApi = newData
              .filter((item: any) => item.isFlag === true)
              .map((item: any) => Number(item.compensationId));

            set((state) => {
              const validVariablePayIds = new Set(
                variablePayList.map((v: any) => Number(v.compensationId)),
              );

              const defaultSelectedVariablePay = (): number[] => {
                const stiLtiIds = variablePayList
                  .filter((v: any) => isVariablePayStiOrLtiByName(v.compensationName))
                  .map((v: any) => Number(v.compensationId));
                const flaggedCustomIds = variablePayList
                  .filter(
                    (v: any) => v.isFlag === true && !isVariablePayStiOrLtiByName(v.compensationName),
                  )
                  .map((v: any) => Number(v.compensationId));
                return [...new Set([...stiLtiIds, ...flaggedCustomIds])];
              };

              let selectedVariablePay = state.selectedVariablePay ?? [];
              const sameReport = state.reportId === reportId;

              selectedVariablePay = selectedVariablePay.filter((id) => validVariablePayIds.has(id));

              if (variablePayList.length === 0) {
                selectedVariablePay = [];
              } else if (!sameReport) {
                selectedVariablePay = defaultSelectedVariablePay();
              } else if (selectedVariablePay.length === 0) {
                selectedVariablePay = defaultSelectedVariablePay();
              }

              const derivedIncludeZero = deriveIncludeZeroVariablePayFromApiList(variablePayList);
              const prevFetchRid = state.lastCompensationFetchReportId;
              const ridNum = Number(reportId);
              const isCompensationRefetch =
                prevFetchRid != null && Number(prevFetchRid) === ridNum;
              const savedIncludeZero = readIncludeZeroVariablePayForReport(ridNum);
              let includeZeroVariablePay: boolean;
              if (isCompensationRefetch) {
                includeZeroVariablePay = state.includeZeroVariablePay ?? true;
              } else if (savedIncludeZero !== undefined) {
                includeZeroVariablePay = savedIncludeZero;
              } else if (typeof derivedIncludeZero === 'boolean') {
                includeZeroVariablePay = derivedIncludeZero;
              } else {
                includeZeroVariablePay = true;
              }

              return {
                compensation: newData,
                selectedCompensation: selectedFromApi,
                variablePay: variablePayList,
                selectedVariablePay,
                includeZeroVariablePay,
                lastCompensationFetchReportId: reportId,
              };
            });
          }
          set({ isLoading: false });
        } catch (error) {
          console.error('Failed to fetch report', error);
          set({ error });
        } finally {
          set({ isLoading: false });
        }
      },

      postCompensation: async (reportId: any, props: any): Promise<any> => {
        const state = get();
        const variablePayList = state.variablePay ?? [];
        const selectedVariablePay = state.selectedVariablePay ?? [];
        const selectedVp = new Set(selectedVariablePay);

        const variablePayPayload =
          variablePayList.length > 0
            ? variablePayList.map((v: any) => ({
                variablePayId: Number(v.compensationId),
                isFlag: selectedVp.has(Number(v.compensationId)),
              }))
            : undefined;

        const compensationPayload = (props ?? []).map((row: any) => ({
          compensationId: Number(row.compensationId),
          isFlag: Boolean(row.isFlag),
        }));

        const numericReportId = typeof reportId === 'number' ? reportId : Number(reportId);

        const saveCompensation: Record<string, unknown> = {
          reportId: Number.isFinite(numericReportId) ? numericReportId : reportId,
          compensation: compensationPayload,
        };
        if (variablePayPayload) {
          saveCompensation.variablePay = variablePayPayload;
          saveCompensation.includeZeroVariablePay = Boolean(state.includeZeroVariablePay);
        }
        try {
          const response: any = await postCompensationData(saveCompensation);
          const { responseCode, data } = response;

          if (responseCode === 200) {
            set({ compensationResponse: data });
          }
        } catch (error) {
          console.error('Failed to fetch countries', error);
          set({ error: String(error) });
        }
      },

      resetReportStore: (newReportID?: any) =>
        set(() => {
          return {
            ...initialState,
            reportId: newReportID ?? null,
          };
        }),
      setDemographic: (data: any) => set({ selectedDemographic: data }),
      getDemographic: async (reportId): Promise<any> => {
        try {
          set({ isLoading: true });
          const response: any = await getDemographic(reportId);
          const { responseCode, data } = response;
          const demographicData = data?.currentReport ?? data?.parentReport ?? [];
          if (responseCode === 200 && demographicData?.length > 0) {
            let newData: any = demographicData.map((value: any) => {
              return {
                ...value,
                selected: false,
              };
            });
            const selectedFromApi = demographicData
              .filter((item: any) => item.isFlag === true)
              .map((item: any) => Number(item.demographicId));

            set({ demographic: newData, selectedDemographic: selectedFromApi });
          }
          set({ isLoading: false });
        } catch (error) {
          console.error('Failed to fetch report', error);
          set({ error });
        } finally {
          set({ isLoading: false });
        }
      },
      postDemographic: async (reportId: any, props: any): Promise<any> => {
        const saveDemographic = {
          reportId: reportId,
          demographic: props,
        };

        try {
          const response: any = await postDemographicData(saveDemographic);
          const { responseCode, data } = response;

          if (responseCode === 200) {
            set({ demographicResponse: data });
          }
        } catch (error) {
          console.error('Failed to fetch countries', error);
          set({ error: String(error) });
        }
      },
      setGroupList: (data: any) => set({ groupList: data }),

      setUserCustomGroups: (data: any) => set({ userCustomGroups: data }),
      getUserCustomGroups: async (reportId: any): Promise<any> => {
        try {
          set({ isLoading: true });
          const response: any = await getUserCustomGroupsData(reportId);
          const { responseCode, data } = response;

          const userCustomGroupsData = data?.currentReport ?? data?.parentReport ?? [];
          if (responseCode === 200 && userCustomGroupsData?.length > 0) {
            let newData: any = userCustomGroupsData.map((group: any) => {
              return {
                id: group.groupId,
                name: group.groupName,
                employeeCount: group.employeeCount,
                fields: group.groupMap.map((ele: any) => ({
                  id: ele.groupColumnId,
                  title: ele.groupColumnValue,
                  selectedOptions: ele.options.map((option: any, index: number) => ({
                    id: index,
                    value: option.value,
                    label: option.value,
                  })),
                })),
              };
            });
            // let newData: any = data.map((value: Record<string, unknown>) => {
            //   return { ...value };
            // });

            set({ userCustomGroups: newData });
          }
          set({ isLoading: false });
        } catch (error) {
          console.error('Failed to fetch report', error);
          set({ error });
        } finally {
          set({ isLoading: false });
        }
      },

      setCustomGroupMetadata: (data: any) => set({ customGroupMetadata: data }),
      getCustomGroupMetadata: async (reportId: any): Promise<any> => {
        try {
          set({ isLoading: true });
          const response: any = await getCustomGroupMetadata(reportId);
          const { responseCode, data } = response;

          const customGroupMetadataData = data?.currentReport ?? data?.parentReport ?? [];
          if (responseCode === 200 && customGroupMetadataData?.length > 0) {
            interface IGroupData {
              id: string;
              title: string;
              options: string[];
            }
            let newData: any = customGroupMetadataData.map((group: IGroupData, index: number) => {
              return {
                id: group.id || index,
                title: group.title,
                options: group.options.map((option: string, optionIndex: number) => {
                  return {
                    id: optionIndex,
                    value: option,
                    label: option,
                  };
                }),
              };
            });

            set({ customGroupMetadata: newData });
          }
          set({ isLoading: false });
        } catch (error) {
          console.error('Failed to fetch report', error);
          set({ error });
        } finally {
          set({ isLoading: false });
        }
      },
      postCustomGroupData: async (reportId: any, inputDataArray: any): Promise<any> => {
        const customGroup = inputDataArray.slice(-1).map((inputData: any) => ({
          customGroupId: Number(inputData.id),
          customGroupName: inputData.name,
          customGroupDetail: inputData.fields
            .filter((field: any) => field.selectedOptions.length > 0)
            .map((field: any) => ({
              optionId: Number(field.id),
              optionValue: field.selectedOptions.map((option: any) => option.value),
            })),
        }));

        if (customGroup[0].customGroupName && customGroup[0].customGroupDetail.length > 0) {
          const savePostCustomGroup = {
            customGroup: customGroup,
            reportId: reportId,
            isRemainingEmployeeGroup: false,
          };

          try {
            const response: any = await postCustomGroupData(savePostCustomGroup);
            const { responseCode, responseMessage } = response;

            if (responseCode === 500) {
              set({ postCustomGroupResponse: { responseCode, responseMessage } });
            }
            if (responseCode === 200) {
              set({ postCustomGroupResponse: { responseCode, responseMessage } });
            }
          } catch (error) {
            console.error('Failed to fetch countries', error);
            set({ error: String(error) });
          }
        }
      },
      clearPreviousPostResponse: () => {
        set({ postCustomGroupResponse: null });
      },
      removeGroupFromList: async (groupId: any, reportId: any): Promise<any> => {
        try {
          set({ isLoading: true });
          const response: any = await removeCustomGroup(groupId, reportId);
        } catch (error) {
          console.error('Failed to fetch missing data', error);
          set({ error });
        } finally {
          set({ isLoading: false });
        }
      },
      setFactors: (list: any, data: any) => {
        let uniqueKeys = new Set(data);
        set({ selectedfactors: [...uniqueKeys], factorsList: list });
      },
      getFactors: async (id: any): Promise<any> => {
        try {
          set({ isLoading: true });
          const response: any = await getFactors(id || 1001);
          const { responseCode, data } = response;
          const factorsData = data?.currentReport ?? data?.parentReport ?? [];
          if (responseCode === 200 && factorsData?.length > 0) {
            const selectedList: any = [];
            let newData: any = factorsData.map((value: any) => {
              if (value.activeFlag) {
                selectedList.push(value.payFactorId);
              }
              return {
                ...value,
                activeFlag: value.activeFlag,
              };
            });
            set({ factorsList: newData, selectedfactors: selectedList });
          }
          set({ isLoading: false });
        } catch (error) {
          console.error('Failed to fetch factors', error);
          set({ error });
        } finally {
          set({ isLoading: false });
        }
      },
      updateFactors: async (id: any, props: any): Promise<any> => {
        const saveFactors = {
          reportId: id || 1001,
          payFactor: props.map((ele: any) => {
            return {
              payFactorId: ele?.payFactorId,
              activeFlag: ele?.activeFlag,
            };
          }),
        };

        try {
          const response: any = await postFactors(saveFactors);
          const { responseCode } = response;

          if (responseCode === 200) {
            // set({ factorsList: data });
          }
        } catch (error) {
          console.error('Failed to fetch factors update', error);
          set({ error: String(error) });
        }
      },
      setIsLoading: (data: any) => {
        set({ isLoading: data });
      },
    }),
    {
      name: 'kf1-newReportStore', // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => window.sessionStorage), // (optional) by default, 'localStorage' is used
    },
  ),
);

export default useNewReportStore;
